import React, { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { PhoneIcon, ClockIcon, ExclamationTriangleIcon, CheckCircleIcon, MapPinIcon } from '@heroicons/react/24/solid';

const EmergencyPage: React.FC = () => {
  const { translations, language } = useLanguage();

  useEffect(() => {
    // Set page title for SEO based on current language
    let titleText = 'Zahnärztlicher Notdienst 24/7 - SeaDent Mallorca';
    let descriptionText = 'Zahnärztlicher Notdienst rund um die Uhr in Palma de Mallorca. Bei Zahnschmerzen und Notfällen sofort für Sie da - Tel: 603 31 96 48';

    if (language === 'en') {
      titleText = 'Dental Emergency Service 24/7 - SeaDent Mallorca';
      descriptionText = 'Round-the-clock dental emergency service in Palma de Mallorca. Immediate help for dental pain and emergencies - Call: 603 31 96 48';
    } else if (language === 'es') {
      titleText = 'Servicio de Urgencias Dentales 24/7 - SeaDent Mallorca';
      descriptionText = 'Servicio de urgencias dentales las 24 horas en Palma de Mallorca. Ayuda inmediata para dolor dental y emergencias - Llame: 603 31 96 48';
    } else if (language === 'ru') {
      titleText = 'Экстренная стоматологическая помощь 24/7 - SeaDent Майорка';
      descriptionText = 'Круглосуточная экстренная стоматологическая помощь в Пальма-де-Майорка. Немедленная помощь при зубной боли и неотложных случаях - Звоните: 603 31 96 48';
    }

    document.title = titleText;

    // Set meta description for SEO
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', descriptionText);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = descriptionText;
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
  }, [language]);

  return (<main className="pt-20 lg:pt-32 pb-16">      {/* Hero section with prominent emergency number */}      <section className="relative bg-gray-900 text-white py-16">        <div className="absolute inset-0 bg-gray-900 opacity-85 z-0" style={{ backgroundImage: "url('/images/SeaDent Palma Teaser Header.jpg')", backgroundSize: 'cover', backgroundPosition: 'center', backgroundBlendMode: 'multiply' }} aria-hidden="true"        ></div>        <div className="container mx-auto px-4 relative z-10">          <div className="max-w-4xl mx-auto text-center">            <div className="inline-block bg-red-600 bg-opacity-90 px-4 py-1 rounded-full mb-4">              <span className="text-white font-bold animate-pulse flex items-center">                <span className="inline-block w-2 h-2 rounded-full bg-white mr-2"></span>                {language === 'de' ? 'JETZT VERFÜGBAR' : (language === 'en' ? 'AVAILABLE NOW' : language === 'ru' ? 'ДОСТУПНО СЕЙЧАС' : 'DISPONIBLE AHORA')}                <span className="inline-block w-2 h-2 rounded-full bg-white ml-2"></span>              </span>            </div>            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-shadow-none">              {translations.emergency.title}            </h1>            <p className="text-xl mb-8 text-shadow-none">              {translations.emergency.intro}            </p>            <div className="flex justify-center flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">              <a href="tel:603319648" className="inline-flex items-center justify-center bg-red-600 text-white hover:bg-red-700 font-bold text-xl py-4 px-8 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-red-600 animate-emergencyPulse shadow-lg" aria-label={translations.emergency.cta}              >                <PhoneIcon className="w-6 h-6 mr-2" aria-hidden="true" />                603 31 96 48              </a>              <a href="https://wa.me/+34603319648" className="inline-flex items-center justify-center bg-green-500 text-white hover:bg-green-600 font-bold text-xl py-4 px-8 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-green-500"              >                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />                </svg>                WhatsApp              </a>            </div>          </div>        </div>      </section>

    {/* Details section */}
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Availability */}
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="flex items-start mb-4">
                <ClockIcon className="w-8 h-8 text-red-600 mr-3 flex-shrink-0" />
                <h2 className="text-2xl font-bold text-gray-800">
                  {translations.emergency.details[0].title}
                </h2>
              </div>
              <p className="text-gray-700 mb-4">
                {translations.emergency.details[0].description}
              </p>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span>{language === 'de' ? 'Wochenende & Feiertage' : (language === 'en' ? 'Weekends & Holidays' : language === 'ru' ? 'Выходные и праздники' : 'Fines de semana y festivos')}</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span>{language === 'de' ? 'Nachts & frühe Morgenstunden' : (language === 'en' ? 'Nights & Early Mornings' : language === 'ru' ? 'Ночью и ранним утром' : 'Noches y madrugadas')}</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span>{language === 'de' ? 'Sofortige Hilfe bei Schmerzen' : (language === 'en' ? 'Immediate Pain Relief' : language === 'ru' ? 'Немедленное облегчение боли' : 'Alivio inmediato del dolor')}</span>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="flex items-start mb-4">
                <PhoneIcon className="w-8 h-8 text-red-600 mr-3 flex-shrink-0" />
                <h2 className="text-2xl font-bold text-gray-800">
                  {translations.emergency.details[1].title}
                </h2>
              </div>
              <p className="text-gray-700 mb-4">
                {translations.emergency.details[1].description}
              </p>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span>{language === 'de' ? 'Telefon: 603 31 96 48' : (language === 'en' ? 'Phone: 603 31 96 48' : language === 'ru' ? 'Телефон: 603 31 96 48' : 'Teléfono: 603 31 96 48')}</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span>{language === 'de' ? 'WhatsApp verfügbar' : (language === 'en' ? 'WhatsApp available' : language === 'ru' ? 'Доступен WhatsApp' : 'WhatsApp disponible')}</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span>{language === 'de' ? 'Mehrsprachiger Service (DE, EN, ES)' : (language === 'en' ? 'Multilingual service (EN, DE, ES)' : language === 'ru' ? 'Многоязычный сервис (RU, EN, DE, ES)' : 'Servicio multilingüe (ES, EN, DE)')}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            {/* Common Emergencies */}
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="flex items-start mb-4">
                <ExclamationTriangleIcon className="w-8 h-8 text-red-600 mr-3 flex-shrink-0" />
                <h2 className="text-2xl font-bold text-gray-800">
                  {translations.emergency.details[2].title}
                </h2>
              </div>
              <p className="text-gray-700 mb-4">
                {translations.emergency.details[2].description}
              </p>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span>{language === 'de' ? 'Starke Zahnschmerzen' : (language === 'en' ? 'Severe tooth pain' : language === 'ru' ? 'Сильная зубная боль' : 'Dolor dental intenso')}</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span>{language === 'de' ? 'Abgebrochene Zähne & Füllungen' : (language === 'en' ? 'Broken teeth & fillings' : language === 'ru' ? 'Сломанные зубы и пломбы' : 'Dientes y empastes rotos')}</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span>{language === 'de' ? 'Zahnfleischentzündungen & Abszesse' : (language === 'en' ? 'Gum inflammation & abscesses' : language === 'ru' ? 'Воспаление десен и абсцессы' : 'Inflamación de encías y abscesos')}</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span>{language === 'de' ? 'Unfallbedingte Zahnverletzungen' : (language === 'en' ? 'Dental injuries from accidents' : language === 'ru' ? 'Травмы зубов при несчастных случаях' : 'Lesiones dentales por accidentes')}</span>
                </li>
              </ul>
            </div>

            {/* Service */}
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="flex items-start mb-4">
                <CheckCircleIcon className="w-8 h-8 text-red-600 mr-3 flex-shrink-0" />
                <h2 className="text-2xl font-bold text-gray-800">
                  {translations.emergency.details[3].title}
                </h2>
              </div>
              <p className="text-gray-700 mb-4">
                {translations.emergency.details[3].description}
              </p>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span>{language === 'de' ? 'Schmerzfreie Behandlung' : (language === 'en' ? 'Pain-free treatment' : language === 'ru' ? 'Безболезненное лечение' : 'Tratamiento sin dolor')}</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span>{language === 'de' ? 'Modernste Technologie' : (language === 'en' ? 'State-of-the-art technology' : language === 'ru' ? 'Современные технологии' : 'Tecnología de vanguardia')}</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span>{language === 'de' ? 'Erfahrenes Notfallteam' : (language === 'en' ? 'Experienced emergency team' : language === 'ru' ? 'Опытная команда экстренной помощи' : 'Equipo de emergencia experimentado')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* What to do section */}
    <section className="py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            {language === 'de' ? 'Was tun im Notfall?' : (language === 'en' ? 'What to do in an emergency?' : language === 'ru' ? 'Что делать в экстренной ситуации?' : '¿Qué hacer en caso de emergencia?')}
          </h2>

          <div className="bg-white p-8 rounded-lg shadow-md mb-8">
            <ol className="space-y-6">
              <li className="flex">
                <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-red-600 text-white font-bold mr-4">1</div>
                <div>
                  <h3 className="font-semibold text-lg text-gray-800">
                    {language === 'de' ? 'Rufen Sie uns sofort an' : (language === 'en' ? 'Call us immediately' : language === 'ru' ? 'Позвоните нам немедленно' : 'Llámenos inmediatamente')}
                  </h3>
                  <p className="text-gray-700">
                    {language === 'de'
                      ? 'Kontaktieren Sie uns unter 603 31 96 48, egal zu welcher Uhrzeit. Beschreiben Sie Ihre Symptome so genau wie möglich.'
                      : (language === 'en'
                        ? 'Contact us at 603 31 96 48, no matter what time it is. Describe your symptoms as accurately as possible.'
                        : language === 'ru'
                          ? 'Свяжитесь с нами по номеру 603 31 96 48, в любое время суток. Опишите свои симптомы как можно точнее.'
                          : 'Contáctenos al 603 31 96 48, sin importar la hora. Describa sus síntomas con la mayor precisión posible.')}
                  </p>
                </div>
              </li>
              <li className="flex">
                <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-red-600 text-white font-bold mr-4">2</div>
                <div>
                  <h3 className="font-semibold text-lg text-gray-800">
                    {language === 'de' ? 'Erste Hilfe Maßnahmen' : (language === 'en' ? 'First aid measures' : language === 'ru' ? 'Меры первой помощи' : 'Medidas de primeros auxilios')}
                  </h3>
                  <p className="text-gray-700">
                    {language === 'de'
                      ? 'Bei Schmerzen: Kühlen Sie die betroffene Stelle von außen. Bei abgebrochenem Zahn: Bewahren Sie das Fragment in Milch oder Speichel auf.'
                      : (language === 'en'
                        ? 'For pain: Cool the affected area from the outside. For a broken tooth: Keep the fragment in milk or saliva.'
                        : language === 'ru'
                          ? 'При боли: Охладите пораженную область снаружи. При сломанном зубе: Храните фрагмент в молоке или слюне.'
                          : 'Para el dolor: enfríe el área afectada desde el exterior. Para un diente roto: conserve el fragmento en leche o saliva.')}
                  </p>
                </div>
              </li>
              <li className="flex">
                <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-red-600 text-white font-bold mr-4">3</div>
                <div>
                  <h3 className="font-semibold text-lg text-gray-800">
                    {language === 'de' ? 'Kommen Sie zu uns' : (language === 'en' ? 'Come to our clinic' : language === 'ru' ? 'Приезжайте в нашу клинику' : 'Venga a nuestra clínica')}
                  </h3>
                  <p className="text-gray-700">
                    {language === 'de'
                      ? 'Unsere Praxis befindet sich in der Avenida de Jaime III № 3, Palma. Wir bereiten alles für Ihre sofortige Behandlung vor.'
                      : (language === 'en'
                        ? 'Our practice is located at Avenida de Jaime III № 3, Palma. We will prepare everything for your immediate treatment.'
                        : language === 'ru'
                          ? 'Наша клиника находится по адресу Avenida de Jaime III № 3, Пальма. Мы подготовим все для вашего немедленного лечения.'
                          : 'Nuestra consulta está ubicada en Avenida de Jaime III № 3, Palma. Prepararemos todo para su tratamiento inmediato.')}
                  </p>
                </div>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </section>

    {/* Location section */}
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            {language === 'de' ? 'Wo Sie uns finden' : (language === 'en' ? 'Where to find us' : language === 'ru' ? 'Где нас найти' : 'Dónde encontrarnos')}
          </h2>

          <div className="bg-white p-8 rounded-lg shadow-md mb-8 flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-6 md:mb-0 md:mr-8">
              <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden">
                {/* This would be replaced with an actual map, but this is a placeholder */}
                <div className="w-full h-full flex items-center justify-center bg-gray-200">
                  <MapPinIcon className="w-12 h-12 text-red-600" />
                </div>
              </div>
            </div>
            <div className="md:w-1/2">
              <h3 className="font-semibold text-xl text-gray-800 mb-3">
                SeaDent Mallorca
              </h3>
              <p className="text-gray-700 mb-4">
                Avenida de Jaime III № 3<br />
                Palma de Mallorca
              </p>
              <p className="text-gray-700 mb-6">
                {language === 'de'
                  ? 'Zentral gelegen im Herzen von Palma, leicht zu erreichen mit öffentlichen Verkehrsmitteln oder dem Auto.'
                  : (language === 'en'
                    ? 'Centrally located in the heart of Palma, easily accessible by public transport or car.'
                    : language === 'ru'
                      ? 'Расположена в центре Пальмы, легко добраться на общественном транспорте или автомобиле.'
                      : 'Ubicado en el centro de Palma, fácilmente accesible en transporte público o coche.')}
              </p>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=Avenida+de+Jaime+III+3+Palma+de+Mallorca"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-red-600 text-white hover:bg-red-700 font-bold py-2 px-4 rounded-lg transition-colors"
              >
                <MapPinIcon className="w-5 h-5 mr-2" />
                {language === 'de' ? 'Wegbeschreibung in Google Maps öffnen' : (language === 'en' ? 'Open directions in Google Maps' : language === 'ru' ? 'Открыть маршрут в Google Maps' : 'Abrir indicaciones en Google Maps')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Bottom CTA */}
    <section className="relative bg-gray-900 text-white py-16">
      <div
        className="absolute inset-0 bg-gray-900 opacity-85 z-0"
        style={{
          backgroundImage: "url('/images/SeaDent Palma Header.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'multiply'
        }}
        aria-hidden="true"
      ></div>
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block bg-red-600 bg-opacity-90 px-4 py-1 rounded-full mb-4">
            <span className="text-white font-bold animate-pulse flex items-center">
              <span className="inline-block w-2 h-2 rounded-full bg-white mr-2"></span>
              {language === 'de' ? '24/7 NOTDIENST' : (language === 'en' ? '24/7 EMERGENCY SERVICE' : language === 'ru' ? 'ЭКСТРЕННАЯ ПОМОЩЬ 24/7' : 'SERVICIO DE URGENCIAS 24/7')}
              <span className="inline-block w-2 h-2 rounded-full bg-white ml-2"></span>
            </span>
          </div>
          <h2 className="text-3xl font-bold mb-6 text-shadow-sm">
            {language === 'de'
              ? 'Zahnschmerzen? Wir sind für Sie da!'
              : (language === 'en'
                ? "Dental pain? We are here for you!"
                : language === 'ru'
                  ? "Зубная боль? Мы здесь, чтобы помочь вам!"
                  : '¿Dolor dental? ¡Estamos aquí para ayudarle!')}
          </h2>
          <p className="text-xl mb-8 text-shadow-sm">
            {language === 'de'
              ? 'Zögern Sie nicht, uns bei Zahnproblemen sofort zu kontaktieren – rund um die Uhr, 365 Tage im Jahr.'
              : (language === 'en'
                ? "Don't hesitate to contact us immediately for dental problems – 24 hours a day, 365 days a year."
                : language === 'ru'
                  ? "Не стесняйтесь обращаться к нам немедленно при проблемах с зубами – 24 часа в сутки, 365 дней в году."
                  : 'No dude en contactarnos inmediatamente si tiene problemas dentales – las 24 horas del día, los 365 días del año.')}
          </p>
        </div>
      </div>
    </section>
  </main>
  );
};

export default EmergencyPage; 